<template>
  <div class="flex flex-col h-full">

    <layout-page-head-section>
      <div class="flex gap-2 xs:gap-4 sm:items-start">
        <div class="flex flex-1 gap-4" v-auto-animate="{duration: 100}" :class="isCondensedHeader ? 'items-center' : null">

          <template v-if="!isCondensedHeader">
            <div class="flex">
              <container-avatar :container="container" size="3xl" />
            </div>
            <div class="flex-1">
              <layout-page-heading>{{container.files_count}} File{{container.files_count !== 1 ? 's' :''}}</layout-page-heading>
              <user-owner-badge v-if="!isEntityOwner" :user="container.user" class="mt-0.5" />
            </div>
          </template>

          <template v-else>
            <container-avatar :container="container" size="sm"/>
            <p class="flex-1 font-serif text-2xl">{{container.files_count}} File{{container.files_count !== 1 ? 's' :''}}</p>
          </template>

        </div>

        <div class="gap-2 flex justify-end items-start">
          <u-dropdown :items="overflowMenu">
            <u-button
              :icon="COMMON_ICONS.moreHorizontal"
              variant="outline"
              color="charcoal"
            />
          </u-dropdown>
        </div>

      </div>
    </layout-page-head-section>

    <div class="flex-1 -mr-[9px] lg:-mr-[29px] overflow-hidden">
      <ClientOnly>
        <file-list
          :context="container"
          :context-type="FILE_CONTEXTS.container"
          :share-token="shareToken"
          :disable-selection="!isAuthenticated"
          :user-id="userId"
          loading-text="Loading Files"
          @list-item-click="goToFile"
        />
      </ClientOnly>
    </div>

    <file-selection-toolbar :actions="fileSelectionActions" :context="container" :context-type="FILE_CONTEXTS.container" :user-id="userId" v-if="isAuthenticated" />

    <nuxt-page :route="route" page-key="container-file-detail" />
  </div>
</template>

<script setup>
  import {storeToRefs} from 'pinia';

  const route = useRoute();
  const {container_id_or_slug: containerId, user_id_or_slug: userId, share_token: shareToken} = route.params;
  const containerStore = useContainersStore();
  const {container} = storeToRefs(containerStore);
  const isEntityOwner = computed(() => container.value?.isOwnedByUser);
  const {isAuthenticated} = useSessionStore();

  const {refresh, pending, error} = await useAsyncData(
    `container-details-${containerId}`,
    () => containerStore.getContainer({id: containerId, userId, shareToken})
  );

  if (error.value) {
    throw createError({
      ...error.value
    });
  }

  useForeverHead({
    title: computed(() => `${container.value.user.name} shared ${container.value.files_count} file(s)`),
    socialImage: container.value.avatarImage
  });

  function goToFile({item: file}) {
    navigateTo(`${route.path.replace(/\/$/, '')}/files/${file.id}${buildQueryString(route.query)}`);
  }


  // condensed header
  const isCondensedHeader = ref(false);
  useEventBus(FILE_LIST_SCROLL_THRESHOLD_EVENT).on(status => {
    isCondensedHeader.value = !!status;
  })


  //context actions
  function slideshow() {
    useSlideshow({
      container: container.value,
      fileContext: FILE_CONTEXTS.container,
      userId,
      shareToken
    }).start();
  }

  function download() {
    useFileActions().downloadContext({
      context: container.value,
      contextType: FILE_CONTEXTS.container,
      userId
    });
  }

  const overflowMenu = [[
    {
      label: 'Download',
      icon: COMMON_ICONS.download,
      click: () => download()
    },
    {
      label: 'View Slideshow',
      icon: COMMON_ICONS.slideshow,
      click: () => slideshow()
    }
  ]];


  //file selection actions
  const allFileSelectionActions = useFileActions().selectionToolbarActions;
  const fileSelectionActions = computed(() => {
    if (isEntityOwner.value) {
      return [
        allFileSelectionActions.organize,
        allFileSelectionActions.share,
        allFileSelectionActions.download,
        allFileSelectionActions.merge
      ];
    }

    if (isAuthenticated) {
      return [
        allFileSelectionActions.download,
        allFileSelectionActions.copyToMyAccount
      ];
    }
  });


  //set up the signup cookie which allows us to link users to the sharer during signup.
  onMounted(async () => {

    if (!isAuthenticated && shareToken) {
      await useCommonModals().openSignupFromShareModal({
        referrer: container.value.user,
        referralSource: 'share-container',
        referralContext: 'share'
      });
    }
  });
</script>
